"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { ChevronDown, Search, UsersRound, X } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import { RouteHero, RouteHeroMark } from "@/features/home-v2/RouteHero";
import ArtistsGrid from "@/features/artists/ArtistsGrid";
import { useArtists } from "@/features/artists/hooks/useArtists";
import type { ArtistsResponse } from "@/features/artists/types";
import { useDebounce } from "@/hooks/useDebounce";
import { sortToParams, useBrowseParams, type SortKey } from "@/lib/browse/params";
import { useGridColumns } from "@/hooks/useGridColumns";
import { pageSizeFor } from "@/lib/grid-rows";
import "./artists.css";
type Props = {
  lang: "fa" | "en";
  dict: Dictionary;
  initialData: ArtistsResponse;
  perPage: number;
  /** The URL set `per_page`: honoured as given, never re-cut to the grid. */
  perPageFixed?: boolean;
  title: string;
};
const ARTIST_SORTS: readonly SortKey[] = ["newest", "oldest", "name-asc", "name-desc", "created-desc", "created-asc"];

/** The URL remains the authority for search, sort and pagination. */
export default function ArtistsPageClient(t0) {
  const $ = _c(116);
  const {
    lang,
    dict,
    initialData,
    perPage: basePerPage,
    perPageFixed: t1,
    title
  } = t0;
  const perPageFixed = t1 === undefined ? false : t1;
  const isFa = lang === "fa";
  const browse = dict.browse;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      sortKeys: ARTIST_SORTS,
      defaultSort: "newest"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    q,
    sort,
    page,
    set,
    clearAll,
    buildHref
  } = useBrowseParams(t2);
  const search = useDebounce(q, 500);
  const grid = useGridColumns();
  let t3;
  if ($[1] !== basePerPage || $[2] !== grid.columns || $[3] !== perPageFixed) {
    t3 = perPageFixed ? basePerPage : pageSizeFor(grid.columns, basePerPage);
    $[1] = basePerPage;
    $[2] = grid.columns;
    $[3] = perPageFixed;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const perPage = t3;
  let t4;
  if ($[5] !== sort) {
    t4 = sortToParams(sort);
    $[5] = sort;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    orderBy,
    order
  } = t4;
  const t5 = search || undefined;
  let t6;
  if ($[7] !== order || $[8] !== orderBy || $[9] !== page || $[10] !== perPage || $[11] !== t5) {
    t6 = {
      page,
      perPage,
      name: t5,
      order,
      order_by: orderBy
    };
    $[7] = order;
    $[8] = orderBy;
    $[9] = page;
    $[10] = perPage;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const queryParams = t6;
  const t7 = !search && sort === "newest" ? initialData : undefined;
  let t8;
  if ($[13] !== t7) {
    t8 = {
      initialData: t7
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const query = useArtists(queryParams, t8);
  const total = query.data?.data?.page?.total;
  let t9;
  if ($[15] !== isFa || $[16] !== total) {
    t9 = total == null ? "\u2014" : new Intl.NumberFormat(isFa ? "fa-IR" : "en-US").format(total);
    $[15] = isFa;
    $[16] = total;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const count = t9;
  const hasActiveFilters = Boolean(q || sort !== "newest");
  let t10;
  if ($[18] !== browse.sortNewest) {
    t10 = {
      key: "newest",
      label: browse.sortNewest
    };
    $[18] = browse.sortNewest;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== browse.sortOldest) {
    t11 = {
      key: "oldest",
      label: browse.sortOldest
    };
    $[20] = browse.sortOldest;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== browse.sortNameAsc) {
    t12 = {
      key: "name-asc",
      label: browse.sortNameAsc
    };
    $[22] = browse.sortNameAsc;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== browse.sortNameDesc) {
    t13 = {
      key: "name-desc",
      label: browse.sortNameDesc
    };
    $[24] = browse.sortNameDesc;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== browse.sortJoinedNewest) {
    t14 = {
      key: "created-desc",
      label: browse.sortJoinedNewest
    };
    $[26] = browse.sortJoinedNewest;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== browse.sortJoinedOldest) {
    t15 = {
      key: "created-asc",
      label: browse.sortJoinedOldest
    };
    $[28] = browse.sortJoinedOldest;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14 || $[35] !== t15) {
    t16 = [t10, t11, t12, t13, t14, t15];
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const sortOptions = t16;
  const t17 = isFa ? "\u062C\u0627\u0645\u0639\u0647\u0654 \u062E\u0627\u0644\u0642\u0627\u0646 NFTBaz" : "NFTBaz creator community";
  const t18 = isFa ? "\u0647\u0646\u0631\u0645\u0646\u062F\u0647\u0627\u06CC \u062F\u0646\u06CC\u0627\u06CC NFT \u0631\u0627 \u067E\u06CC\u062F\u0627 \u06A9\u0646\u06CC\u062F\u060C \u0622\u062B\u0627\u0631\u0634\u0627\u0646 \u0631\u0627 \u0628\u0628\u06CC\u0646\u06CC\u062F \u0648 \u062E\u0627\u0644\u0642\u0627\u0646\u06CC \u0631\u0627 \u06A9\u0647 \u062F\u0648\u0633\u062A \u062F\u0627\u0631\u06CC\u062F \u062F\u0646\u0628\u0627\u0644 \u06A9\u0646\u06CC\u062F." : "Discover NFT artists, explore their work and follow the creators who inspire you.";
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <UsersRound aria-hidden={true} />;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = isFa ? "\u0647\u0646\u0631\u0645\u0646\u062F \u0628\u0631\u0627\u06CC \u06A9\u0634\u0641" : "artists to discover";
  const t21 = isFa ? `${count} هنرمند` : `${count} artists`;
  let t22;
  if ($[38] !== count || $[39] !== t20 || $[40] !== t21) {
    t22 = <RouteHeroMark icon={t19} value={count} caption={t20} ariaLabel={t21} />;
    $[38] = count;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t17 || $[43] !== t18 || $[44] !== t22 || $[45] !== title) {
    t23 = <RouteHero eyebrow={t17} title={title} description={t18} aside={t22} />;
    $[42] = t17;
    $[43] = t18;
    $[44] = t22;
    $[45] = title;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const t24 = isFa ? "\u062C\u0633\u062A\u200C\u0648\u062C\u0648 \u0648 \u0645\u0631\u062A\u0628\u200C\u0633\u0627\u0632\u06CC \u0647\u0646\u0631\u0645\u0646\u062F\u0627\u0646" : "Search and sort artists";
  let t25;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Search aria-hidden={true} />;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== browse.searchArtists) {
    t26 = <label className="v2-sr-only" htmlFor="artists-search">{browse.searchArtists}</label>;
    $[48] = browse.searchArtists;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== set) {
    t27 = event => set({
      q: event.target.value || null
    }, {
      history: "replace"
    });
    $[50] = set;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== browse.searchArtists || $[53] !== q || $[54] !== t27) {
    t28 = <input id="artists-search" type="search" value={q} onChange={t27} placeholder={browse.searchArtists} />;
    $[52] = browse.searchArtists;
    $[53] = q;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== browse.clearSearch || $[57] !== q || $[58] !== set) {
    t29 = q ? <button type="button" className="v2-artists-clear v2-focus" onClick={() => set({
      q: null
    }, {
      history: "replace"
    })} aria-label={browse.clearSearch}><X aria-hidden={true} /></button> : null;
    $[56] = browse.clearSearch;
    $[57] = q;
    $[58] = set;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t26 || $[61] !== t28 || $[62] !== t29) {
    t30 = <div className="v2-artists-search">{t25}{t26}{t28}{t29}</div>;
    $[60] = t26;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const t31 = isFa ? "\u0645\u0631\u062A\u0628\u200C\u0633\u0627\u0632\u06CC" : "Sort by";
  let t32;
  if ($[64] !== t31) {
    t32 = <label className="v2-sr-only" htmlFor="artists-sort">{t31}</label>;
    $[64] = t31;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== set) {
    t33 = event_0 => set({
      sort: event_0.target.value as SortKey
    });
    $[66] = set;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== sortOptions) {
    t34 = sortOptions.map(_temp);
    $[68] = sortOptions;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== sort || $[71] !== t33 || $[72] !== t34) {
    t35 = <select id="artists-sort" value={sort} onChange={t33}>{t34}</select>;
    $[70] = sort;
    $[71] = t33;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <ChevronDown aria-hidden={true} />;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  let t37;
  if ($[75] !== t32 || $[76] !== t35) {
    t37 = <div className="v2-artists-sort">{t32}{t35}{t36}</div>;
    $[75] = t32;
    $[76] = t35;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== t24 || $[79] !== t30 || $[80] !== t37) {
    t38 = <section className="v2-artists-tools" aria-label={t24}>{t30}{t37}</section>;
    $[78] = t24;
    $[79] = t30;
    $[80] = t37;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  const t39 = isFa ? "\u0641\u0647\u0631\u0633\u062A \u0647\u0646\u0631\u0645\u0646\u062F\u0627\u0646" : "Artist directory";
  let t40;
  if ($[82] !== t39) {
    t40 = <h2 className="v2-sr-only">{t39}</h2>;
    $[82] = t39;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  const t41 = isFa ? "\u0647\u0646\u0631\u0645\u0646\u062F" : "artists";
  let t42;
  if ($[84] !== count || $[85] !== t41) {
    t42 = <span className="v2-sr-only" aria-live="polite">{count} {t41}</span>;
    $[84] = count;
    $[85] = t41;
    $[86] = t42;
  } else {
    t42 = $[86];
  }
  let t43;
  if ($[87] !== clearAll || $[88] !== dict.states || $[89] !== hasActiveFilters) {
    t43 = hasActiveFilters ? <button type="button" onClick={() => clearAll()} className="v2-artists-reset v2-focus">{dict.states.clearFilters}</button> : null;
    $[87] = clearAll;
    $[88] = dict.states;
    $[89] = hasActiveFilters;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] !== t42 || $[92] !== t43) {
    t44 = <div className="v2-artists-results-meta">{t42}{t43}</div>;
    $[91] = t42;
    $[92] = t43;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] !== t40 || $[95] !== t44) {
    t45 = <div className="v2-artists-results-head">{t40}{t44}</div>;
    $[94] = t40;
    $[95] = t44;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== buildHref) {
    t46 = target => buildHref({
      page: target
    }, false);
    $[97] = buildHref;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== clearAll) {
    t47 = () => clearAll();
    $[99] = clearAll;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] !== dict || $[102] !== grid.ref || $[103] !== hasActiveFilters || $[104] !== lang || $[105] !== page || $[106] !== perPage || $[107] !== query || $[108] !== t46 || $[109] !== t47) {
    t48 = <ArtistsGrid lang={lang} dict={dict} query={query} page={page} pageSize={perPage} gridRef={grid.ref} buildPageHref={t46} hasActiveFilters={hasActiveFilters} onClearFilters={t47} />;
    $[101] = dict;
    $[102] = grid.ref;
    $[103] = hasActiveFilters;
    $[104] = lang;
    $[105] = page;
    $[106] = perPage;
    $[107] = query;
    $[108] = t46;
    $[109] = t47;
    $[110] = t48;
  } else {
    t48 = $[110];
  }
  let t49;
  if ($[111] !== t23 || $[112] !== t38 || $[113] !== t45 || $[114] !== t48) {
    t49 = <main className="v2-page v2-artists-index" id="v2-main">{t23}{t38}{t45}{t48}</main>;
    $[111] = t23;
    $[112] = t38;
    $[113] = t45;
    $[114] = t48;
    $[115] = t49;
  } else {
    t49 = $[115];
  }
  return t49;
}
function _temp(t0) {
  const {
    key,
    label
  } = t0;
  return <option value={key} key={key}>{label}</option>;
}